// src/app/shared/interceptors/auth.interceptor.ts
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { AuthService } from '../shared/services/auth.service';

// Shared state for token refresh
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<boolean>(false);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>, 
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  
  // Skip interceptor for certain URLs
  if (req.url.includes('assets') || req.url.includes('silent-check-sso')) {
    return next(req);
  }
  
  // Add token to request
  const token = localStorage.getItem('keycloakToken');
  let authReq = req;
  if (token) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
  
  return next(authReq).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse) {
        console.log(`HTTP error: ${error.status} for ${req.url}`);
        
        if (error.status === 0) {
          console.error('Network error - cannot reach server');
          return throwError(() => new Error('Network error - server unreachable'));
        }
        
        if (error.status === 401) {
          return handle401Error(authReq, next, authService);
        }
        
        if (error.status === 502) {
          console.error('Bad Gateway error detected in interceptor', error);
          return throwError(() => error);
        }
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(request: HttpRequest<unknown>, next: HttpHandlerFn, authService: AuthService): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(false);

    return from(authService.refreshCurrentToken()).pipe(
      switchMap(() => {
        isRefreshing = false;
        refreshTokenSubject.next(true);
        
        // Add the refreshed token
        const newToken = localStorage.getItem('keycloakToken');
        const authReq = request.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` }
        });
        
        return next(authReq);
      }),
      catchError(error => {
        isRefreshing = false;
        authService.logout();
        return throwError(() => error);
      }),
      finalize(() => {
        isRefreshing = false;
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(refreshed => refreshed),
      take(1),
      switchMap(() => {
        const newToken = localStorage.getItem('keycloakToken');
        const authReq = request.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` }
        });
        return next(authReq);
      })
    );
  }
}